import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  type CognitoUserSession,
  type IAuthenticationCallback,
} from 'amazon-cognito-identity-js'
import type {
  LoginCredentials,
  RegisterCredentials,
  AuthUser,
  UserRole,
} from '@/types/auth.types'

// ── Pool singleton ─────────────────────────────────────────────

export const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
  ClientId:   import.meta.env.VITE_COGNITO_CLIENT_ID as string,
})

// ── Helpers ────────────────────────────────────────────────────

function cognitoUserFor(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: userPool })
}

function sessionToAuthUser(session: CognitoUserSession): AuthUser {
  const idToken     = session.getIdToken()
  const accessToken = session.getAccessToken()
  const refreshToken= session.getRefreshToken()

  const payload = idToken.decodePayload()
  const groups: string[] = payload['cognito:groups'] ?? []

  let role: UserRole = 'USER'
  if (groups.includes('Admins'))    role = 'ADMIN'
  else if (groups.includes('Operators')) role = 'OPERATOR'

  const name      = (payload['name'] as string) ?? (payload['email'] as string)
  const given     = payload['given_name']  as string | undefined
  const family    = payload['family_name'] as string | undefined
  // Fallback derivato dal full name (split sul primo spazio)
  const [fnFromName, ...lnRest] = (name ?? '').split(' ')

  return {
    sub:          payload['sub'] as string,
    email:        payload['email'] as string,
    name,
    firstName:    given  || fnFromName        || undefined,
    lastName:     family || lnRest.join(' ')  || undefined,
    role,
    accessToken:  accessToken.getJwtToken(),
    idToken:      idToken.getJwtToken(),
    refreshToken: refreshToken.getToken(),
    tokenExpiry:  accessToken.getExpiration() * 1000,
  }
}

// ── Auth operations ────────────────────────────────────────────

/**
 * Sign in with email + password.
 * Resolves with AuthUser or rejects with CognitoError.
 * The promise can also resolve with { mfaRequired: true, session, mfaType }
 * when MFA is needed — handle in useCognito hook.
 */
export function cognitoSignIn(
  credentials: LoginCredentials
): Promise<AuthUser | { mfaRequired: true; session: string; mfaType: string }> {
  const { email, password } = credentials

  return new Promise((resolve, reject) => {
    const user = cognitoUserFor(email)
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    })

    const callbacks: IAuthenticationCallback = {
      onSuccess: (session) => resolve(sessionToAuthUser(session)),

      onFailure: (err) => reject(err),

      // MFA required — forward challenge info to the UI
      totpRequired: (_challengeName, session) =>
        resolve({ mfaRequired: true, session, mfaType: 'SOFTWARE_TOKEN_MFA' }),

      mfaRequired: (_challengeName, session) =>
        resolve({ mfaRequired: true, session, mfaType: 'SMS_MFA' }),
    }

    user.authenticateUser(authDetails, callbacks)
  })
}

/**
 * Register a new account (USER or OPERATOR).
 * - `userType` discrimina il ruolo: il post-confirmation trigger Cognito decide
 *   l'assegnazione al gruppo `Operators` in base a questo attributo.
 * - `inviteToken` è incluso solo se presente (richiesto solo per OPERATOR).
 */
export function cognitoSignUp(credentials: RegisterCredentials): Promise<void> {
  const { email, password, firstName, lastName, userType, inviteToken } = credentials

  const attributes = [
    new CognitoUserAttribute({ Name: 'email',           Value: email }),
    new CognitoUserAttribute({ Name: 'given_name',      Value: firstName }),
    new CognitoUserAttribute({ Name: 'family_name',     Value: lastName }),
    new CognitoUserAttribute({ Name: 'name',            Value: `${firstName} ${lastName}` }),
    new CognitoUserAttribute({ Name: 'custom:userType', Value: userType }),
  ]
  if (inviteToken) {
    attributes.push(new CognitoUserAttribute({ Name: 'custom:inviteToken', Value: inviteToken }))
  }

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, attributes, [], (err) => {
      if (err) return reject(err)
      resolve()
    })
  })
}


/** Resend confirmation code to the given email */
export function cognitoResendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUserFor(email).resendConfirmationCode((err) => {
      if (err) return reject(err)
      resolve()
    })
  })
}

/** Confirm sign-up with verification code */
export function cognitoConfirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    console.debug('[cognitoConfirmSignUp] email:', email, '| code:', JSON.stringify(code))
    cognitoUserFor(email).confirmRegistration(code, true, (err) => {
      if (err) {
        console.error('[cognitoConfirmSignUp] error:', err)
        return reject(err)
      }
      resolve()
    })
  })
}

/** Get current authenticated user from session (persisted in localStorage) */
export function cognitoGetCurrentUser(): Promise<AuthUser | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser()
    if (!user) return resolve(null)

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null)
      resolve(sessionToAuthUser(session))
    })
  })
}

/** Refresh tokens using the stored refresh token */
export function cognitoRefreshSession(): Promise<AuthUser | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser()
    if (!user) return resolve(null)

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) return resolve(null)

      user.refreshSession(session.getRefreshToken(), (refreshErr, newSession) => {
        if (refreshErr || !newSession) return resolve(null)
        resolve(sessionToAuthUser(newSession))
      })
    })
  })
}

/** Sign out — clears Cognito local storage */
export function cognitoSignOut(): void {
  userPool.getCurrentUser()?.signOut()
}

/** Cambia password dell'utente loggato (richiede la old password). */
export function cognitoChangePassword(oldPassword: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser()
    if (!user) return reject(new Error('NOT_AUTHENTICATED'))
    user.getSession((sessErr: Error | null) => {
      if (sessErr) return reject(sessErr)
      user.changePassword(oldPassword, newPassword, (err) => {
        if (err) return reject(err)
        resolve()
      })
    })
  })
}

/** Trigger forgot-password flow */
export function cognitoForgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUserFor(email).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

/** Confirm new password with reset code */
export function cognitoConfirmPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUserFor(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}
